import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Utensils, Map, Bike, MapPin, Settings, LogOut, Tag, Bell, BellOff, Volume2, VolumeX, Package2, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/lib/auth';
import { Logo } from '@/components/Logo';
import { OpenClosedBadge } from '@/lib/schedule';
import {
  isSoundEnabled, setSoundEnabled,
  isBrowserNotifEnabled, setBrowserNotifEnabled,
  browserNotifSupported, browserNotifPermission, requestBrowserNotifPermission,
  playOrderChime,
} from '@/lib/notifications';

const items = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/orders', icon: ClipboardList, label: 'Ordini' },
  { to: '/admin/live-map', icon: MapPin, label: 'Mappa live' },
  { to: '/admin/menu', icon: Utensils, label: 'Menu' },
  { to: '/admin/combos', icon: Package2, label: 'Combo/Menù' },
  { to: '/admin/discount-codes', icon: Tag, label: 'Codici sconto' },
  { to: '/admin/zones', icon: Map, label: 'Zone consegna' },
  { to: '/admin/riders', icon: Bike, label: 'Riders' },
  { to: '/admin/settings', icon: Settings, label: 'Impostazioni' },
];

/**
 * Mobile admin tab bar with visible scroll affordances:
 *  - Fades on left/right edges to indicate more content off-screen.
 *  - Chevron buttons that scroll one tab-width when tapped.
 *  - Auto-scrolls the active tab into view on route change.
 */
const MobileAdminTabs = ({ items }) => {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    // scroll active tab into view on mount / route change
    const active = el.querySelector('[data-active="true"]');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
    }
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="lg:hidden relative border-b border-[--brand-border] bg-white">
      {/* Left fade + chevron */}
      <button
        type="button"
        aria-label="Scorri a sinistra"
        onClick={() => scrollBy(-1)}
        className={`absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-r from-white via-white/90 to-transparent transition-opacity ${canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="admin-tabs-scroll-left"
      >
        <ChevronLeft size={18} className="text-[--brand-primary]" />
      </button>

      <div
        ref={scrollerRef}
        className="overflow-x-auto hide-scrollbar"
        data-testid="admin-tabs-scroller"
      >
        <div className="flex gap-1 p-2 min-w-max">
          {items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === '/admin'}
              className={({ isActive }) => `px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${isActive ? 'bg-[--brand-primary] text-white' : 'bg-[--brand-surface-2]'}`}
            >
              {({ isActive }) => (
                <span data-active={isActive || undefined}>{it.label}</span>
              )}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Right fade + chevron */}
      <button
        type="button"
        aria-label="Scorri a destra"
        onClick={() => scrollBy(1)}
        className={`absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-l from-white via-white/90 to-transparent transition-opacity ${canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="admin-tabs-scroll-right"
      >
        <ChevronRight size={18} className="text-[--brand-primary]" />
      </button>
    </div>
  );
};

export const AdminLayout = ({ children, testid }) => {
  const nav = useNavigate();
  const { user, logout } = useAuthStore();
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [notifOn, setNotifOn] = useState(isBrowserNotifEnabled());
  const [notifPerm, setNotifPerm] = useState(browserNotifPermission());

  const toggleSound = () => {
    const v = !soundOn;
    setSoundOn(v);
    setSoundEnabled(v);
    if (v) playOrderChime({ volume: 0.4 });
  };
  const toggleBrowserNotif = async () => {
    if (!browserNotifSupported()) return;
    if (notifPerm !== 'granted') {
      const p = await requestBrowserNotifPermission();
      setNotifPerm(p);
      if (p !== 'granted') return;
    }
    const v = !notifOn;
    setNotifOn(v);
    setBrowserNotifEnabled(v);
  };

  return (
    <div className="min-h-screen bg-[--brand-cream]">
      <header className="min-h-14 border-b border-[--brand-border] bg-white flex items-center px-4 sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <Logo variant="compact" />
        <div className="ml-4 hidden sm:block"><OpenClosedBadge compact /></div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSound}
            title={soundOn ? 'Suono attivo — click per disattivare' : 'Suono disattivato — click per attivare'}
            className={soundOn ? 'text-[--brand-primary]' : 'text-[--brand-muted]'}
            data-testid="admin-topbar-sound-toggle"
          >
            {soundOn ? <Volume2 size={18}/> : <VolumeX size={18}/>}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleBrowserNotif}
            title={
              notifPerm !== 'granted'
                ? 'Notifiche browser: click per abilitare'
                : notifOn ? 'Notifiche browser attive' : 'Notifiche browser disattivate'
            }
            className={notifOn && notifPerm === 'granted' ? 'text-[--brand-primary]' : 'text-[--brand-muted]'}
            data-testid="admin-topbar-notif-toggle"
          >
            {notifOn && notifPerm === 'granted' ? <Bell size={18}/> : <BellOff size={18}/>}
          </Button>
          <div className="text-sm text-[--brand-muted] hidden sm:block ml-2">{user?.email}</div>
          <Button variant="secondary" size="sm" className="rounded-lg" onClick={() => { logout(); nav('/admin/login'); }} data-testid="admin-logout-button"><LogOut size={14} className="mr-1"/>Esci</Button>
        </div>
      </header>
      <div className="flex">
        <aside className="w-60 shrink-0 border-r border-[--brand-border] bg-white min-h-[calc(100vh-56px)] hidden lg:block">
          <nav className="p-3 space-y-1">
            {items.map(it => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === '/admin'}
                className={({isActive}) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? 'bg-[--brand-surface-2] text-[--brand-primary] font-semibold border-l-4 border-[--brand-primary] pl-2' : 'hover:bg-[--brand-surface-2]'}`
                }
                data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
              >
                <it.icon size={16}/>{it.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0" data-testid={testid || 'admin-content'}>
          <MobileAdminTabs items={items} />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6">{children}</div>
        </main>
      </div>
    </div>
  );
};
